/**
 * Configuration file management
 * Handles loading, parsing, and saving Safetrax config files
 */

import {
  parseConfigFile,
  serializeConfigFile,
  getConfigByteCount,
  getChecksumIndex,
  calculateChecksum
} from './utils.js';

export class ConfigManager {
  constructor() {
    this._configs = null;
  }

  /**
   * Fetch and cache the configs bundle
   */
  async _loadBundle() {
    if (this._configs) return this._configs;
    const response = await fetch('/configs.json');
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    this._configs = await response.json();
    return this._configs;
  }

  /**
   * List available configs
   * @returns {Promise<Array>} Array of {filename, name}
   */
  async listConfigs() {
    try {
      const configs = await this._loadBundle();
      return Object.entries(configs).map(([filename, { name }]) => ({ filename, name }));
    } catch (err) {
      console.error('Failed to list configs:', err);
      throw err;
    }
  }

  /**
   * Load a config by filename from the cached bundle
   * @param {string} filename - Config filename
   * @returns {Promise<object>} Parsed config data
   */
  async loadConfig(filename) {
    try {
      const configs = await this._loadBundle();
      const entry = configs[filename];
      if (!entry) {
        throw new Error(`Config not found: ${filename}`);
      }
      return parseConfigFile(entry.data);
    } catch (err) {
      console.error(`Failed to load config ${filename}:`, err);
      throw err;
    }
  }

  /**
   * Create default config bytes
   * @param {string} version - Firmware version (e.g., "1.50", "1.55")
   * @returns {Uint8Array} Default config (version-specific byte count)
   */
  createDefaultConfig(version = null) {
    const byteCount = getConfigByteCount(version);
    const checksumIndex = getChecksumIndex(version);
    const bytes = new Uint8Array(byteCount);
    
    console.log(`[ConfigManager] Creating default config for v${version || '1.55'}: ${byteCount} bytes`);
    
    // Set default values based on setParams() from ros.ino
    bytes[0] = 0x18;      // EE_FLAGS (EFLG_XT_EN | EFLG_YT_EN)
    bytes[1] = 50;        // EE_SW_HOLD_TIME (5 seconds)
    bytes[2] = 45;        // EE_WARN_LED_TIME (4.5 seconds)
    bytes[3] = 20;        // EE_WLED_ON_TIME (200ms)
    bytes[4] = 80;        // EE_WLED_OFF_TIME (800ms)
    bytes[5] = 150;       // EE_ALM_PEND_TIME (15 seconds)
    bytes[6] = 80;        // EE_ALM_ON_TIME (8 seconds)
    bytes[7] = 40;        // EE_ALED_ON_TIME (400ms)
    bytes[8] = 60;        // EE_ALED_OFF_TIME (600ms)
    bytes[9] = 60;        // EE_BUZ_A_ON_TIME (600ms)
    bytes[10] = 40;       // EE_BUZ_A_OFF_TIME (400ms)
    bytes[11] = 200;      // EE_ALM_DONE_TIME (20 seconds)
    bytes[12] = 150;      // EE_LED_PWRUP_TIME (1.5 seconds)
    bytes[13] = 50;       // EE_BUZ_PWRUP_TIME (500ms)
    bytes[14] = 40;       // EE_WRO_ANGLEX (20.0 degrees)
    bytes[15] = 64;       // EE_WRO_ANGLEY (32.0 degrees)
    bytes[16] = 6;        // EE_WRO_TIME (0.6 seconds)
    bytes[17] = 255;      // EE_WRO_ROTATION (unused)
    bytes[18] = 100;      // EE_ARO_ANGLE (50.0 degrees) - was incorrectly at bytes[17]
    bytes[19] = 10;       // EE_ARO_TIME (1.0 second) - was incorrectly at bytes[18]
    bytes[20] = 0x40;     // EE_CTRL1_XL
    bytes[21] = 0x40;     // EE_CTRL2_G
    bytes[22] = 255;      // EE_TAP_THS_6D (unused)
    bytes[23] = 0x0F;     // EE_A_WRIST_TILT_LAT
    bytes[24] = 0x20;     // EE_A_WRIST_TILT_THS
    bytes[25] = 255;      // EE_ARO_ROTATION (unused)
    bytes[26] = 10;       // EE_BUZ_W_ON_TIME (100ms)
    bytes[27] = 90;       // EE_BUZ_W_OFF_TIME (900ms)
    bytes[28] = 255;      // EE_IMPACT_ACC (unused)
    bytes[29] = 30;       // EE_WARN_GPS_TIME (3.0 seconds)
    bytes[30] = 125;      // EE_BATT_LOW (12.5V)
    bytes[31] = 115;      // EE_BATT_FLAT (11.5V)
    bytes[32] = 100;      // EE_BATT_HYST (1.0V)
    bytes[33] = 30;       // EE_SWB_DROP (3.0V)
    bytes[34] = 240;      // EE_KEYOFF_5V_MAX (60 hours)
    bytes[35] = 240;      // EE_KEYOFF_SWB_MAX (60 hours)
    bytes[36] = 255;      // EE_PCB_TYPE (unknown)
    bytes[37] = 255;      // EE_PCB_REV (unknown)
    bytes[38] = 6;        // EE_ALARM_RPT_TIME (6 minutes)
    bytes[39] = 14;       // EE_ALARM_RPT_COUNT (14 repeats)
    bytes[40] = 64;       // EE_GAIN_A (0.25 filter gain)
    bytes[41] = 255;      // EE_GAIN_G (1.0 filter gain)
    bytes[42] = 227;      // EE_CF_RATIO (0.888)
    bytes[43] = 45;       // EE_MAX_ACCXY (45 degrees)
    bytes[44] = 255;      // EE_LOW_BATT_TIME (disabled)
    bytes[45] = 0x00;     // EE_FLAGS2
    bytes[46] = 0x00;     // EE_CTRL7_G
    bytes[47] = 0x01;     // EE_CTRL8_XL
    bytes[48] = 48;       // EE_KEYOFF_5V_MIN (12 hours)
    bytes[49] = 48;       // EE_KEYOFF_SWB_MIN (12 hours)
    bytes[50] = 255;      // EE_SPARE1
    bytes[51] = 255;      // EE_SPARE2
    bytes[52] = 255;      // EE_SPARE3
    bytes[53] = 255;      // EE_SPARE4
    bytes[54] = 255;      // EE_SPARE5
    bytes[55] = 255;      // EE_SPARE6
    
    // Note: v1.50 only uses bytes 0-47, bytes 48-55 are ignored
    // v1.55+ uses all bytes 0-55
    
    // Calculate and set checksum (version-aware)
    bytes[checksumIndex] = calculateChecksum(bytes, version);
    
    return bytes;
  }

  /**
   * Update config bytes with modified values
   * @param {Uint8Array} bytes - Original config bytes
   * @param {object} changes - Changes to apply
   * @param {string} version - Firmware version (optional, inferred from byte count)
   * @returns {Uint8Array} Updated config bytes
   */
  updateConfig(bytes, changes, version = null) {
    const updated = new Uint8Array(bytes);
    
    // Infer version from byte count if not provided
    if (!version) {
      version = bytes.length === 49 ? '1.50' : '1.55';
    }
    
    const checksumIndex = getChecksumIndex(version);
    
    // Determine orientation (check if xAxisForward will be changed)
    let xAxisForward = (updated[0] & 0x20) !== 0;
    if (changes.xAxisForward !== undefined) {
      xAxisForward = changes.xAxisForward;
      
      // Set or clear bit 5 of EE_FLAGS
      if (changes.xAxisForward) {
        updated[0] |= 0x20;  // Set bit 5
      } else {
        updated[0] &= ~0x20; // Clear bit 5
      }
    }
    
    // Apply angle changes
    // The meaning of ANGLEX and ANGLEY depends on orientation:
    // Y-axis forward (default): bytes[14] = roll, bytes[15] = pitch
    // X-axis forward: bytes[14] = pitch, bytes[15] = roll
    if (changes.rollWarning !== undefined) {
      const byteIndex = xAxisForward ? 15 : 14;
      updated[byteIndex] = Math.round(changes.rollWarning * 2);
    }
    
    if (changes.pitchWarning !== undefined) {
      const byteIndex = xAxisForward ? 14 : 15;
      updated[byteIndex] = Math.round(changes.pitchWarning * 2);
    }
    
    if (changes.alarmAngle !== undefined) {
      updated[18] = Math.round(changes.alarmAngle * 2);  // EE_ARO_ANGLE is at byte 18, not 17
    }
    
    if (changes.buzzerEnabled !== undefined) {
      // Enable/disable warning buzzer by setting on-time
      updated[26] = changes.buzzerEnabled ? 10 : 0;
    }
    
    // Recalculate checksum (version-aware)
    updated[checksumIndex] = calculateChecksum(updated, version);
    
    return updated;
  }
}
